import { BUTTON_LABELS, TABLE_LABELS, TAG_LABELS } from '@/constants/labels';
import { IManagedUserRecentSummary, IManagedUserSession } from '@/types';
import { ManagerTableCellLabel as Label } from '../ManagerTableCellLabel';
import { ManagerTableLinkButton as LinkButton } from '../ManagerTableLinkButton';
import { PassFailTag } from '@/components/Tags';
import { useMemo } from 'react';

interface RenderProps {
  mostRecentPractice: IManagedUserSession | null;
  onViewScorecardClick: (userId: string, data: IManagedUserSession) => void;
  record: IManagedUserRecentSummary;
}

/**
 * Practice status cell for the manager dashboard table. This component is responsible for 
 * displaying the practice status for a user, along with any relevant actions (view scorecard).
 *
 * The logic for displaying the practice status is as follows:
 *  - If there is no practice attempt, we display "None taken"
 *  - If there is a practice attempt, we display either "Pass" or "Fail"
 *    based on the result of the most recent attempt, along with a view button
 *    to view the scorecard for that practice attempt.
 */
export const ManagerTableCellPracticeStatus: React.FC<RenderProps> = ({
  mostRecentPractice,
  onViewScorecardClick,
  record,
}) => {
  /**
   * If there is a practice attempt, we display either "Pass" or "Fail"
   * based on the result of the most recent attempt, along with a view button
   * to view the scorecard for that practice attempt.
   */
  const status = useMemo(() => {
    if (mostRecentPractice?.attemptCount === 0) {
      return 'not attempted';
    }

    if (mostRecentPractice?.passed) {
      return 'pass';
    }

    return 'fail';
  }, [mostRecentPractice]);

  // if there is no practice attempt, we display "None taken"
  if (mostRecentPractice === null) {
    return <Label label={TABLE_LABELS.NONE_TAKEN} />;
  }

  // ==============================================
  // Rendering logic
  // ==============================================
  return (
    <div className="flex items-center justify-start gap-2">
      <PassFailTag
        className="antialiased"
        showIcon={false}
        simulationType="practice"
        variant={status}
      />
      <LinkButton
        label={BUTTON_LABELS.VIEW}
        onClick={() => onViewScorecardClick(record.userId, mostRecentPractice)}
      />
    </div>
  );
};

ManagerTableCellPracticeStatus.displayName = 'ManagerTableCellPracticeStatus';

export default ManagerTableCellPracticeStatus;
